<template>
  <layout>
    <template v-slot:default>
      <card :title="'Dropship Shipments (' + dropships.length + ')'" v-if="dropships.length" class="mt-3"
            :collapse="true">
        <div v-for="shipment in dropships" :key="shipment.id"
             :class="{'mb-2 pb-2 border-bottom': dropships.length > 1 }">
          <p class="mb-0">Dropship by: <span class="text-secondary">{{ shipment.dropship_manufacturer_name }}</span></p>
          <ul class="list-unstyled">
            <li v-for="item in shipment.items" :key="item.id" class="text-secondary ml-2">{{ item.sku }} {{ item.name }}
              x {{ item.qty }}
            </li>
          </ul>
          <p class="mb-0">Prepared at: <span class="text-secondary">{{
              moment(shipment.date).format('M/D/YY h:mm A')
            }}</span></p>
          <p class="mb-0">Status: <span class="text-secondary">{{ shipment.status_desc }}</span></p>

          <div v-if="shipment.status == 1 || shipment.status == 2">
            <inertia-link :href="route('admin.orders.shipment.cancel', shipment)" preserve-scroll>Cancel Shipment
            </inertia-link>
            <br/>
            <inertia-link :href="route('admin.orders.shipment.shipped', shipment)" preserve-scroll>Mark Shipped
            </inertia-link>
            <br/>
          </div>
          <div v-else-if="shipment.status == 6">
            <inertia-link :href="route('admin.orders.shipment.delivered', shipment)" preserve-scroll>Mark Delivered
            </inertia-link>
            <br/>
          </div>
        </div>
      </card>

      <card :title="'Shipment History (' + shipmentHistory.length + ')'" v-if="shipmentHistory.length" class="mt-3"
            :collapse="true">
        <div v-for="shipment in shipmentHistory" :key="shipment.id" class="mb-4">
          <div class="row">
            <div class="col-12 col-md-6 col-lg-4 mb-2 mb-lg-0">
              <card :title="`#${shipment.id} - Opened at: ${moment(shipment.date).format('M/D/YY h:mm A')}`"
                    icon="fas fa-tags" headerStyle="primary" class="h-100">
                <ul class="list-unstyled">
                  <li class="px-2 py-1 d-flex justify-content-between">
                    <strong>Prepared at:</strong> <span
                    class="text-secondary">{{ moment(shipment.date).format('M/D/YY h:mm A') }}</span>
                  </li>
                  <li class="px-2 py-1 d-flex justify-content-between">
                    <strong>Status:</strong> <span class="text-secondary">{{ shipment.status_desc }}</span>
                  </li>
                  <hr class="my-2">
                  <li v-if="shipment.status == 6 || shipment.status == 15" class="px-2 py-1">
                    <div class="d-flex justify-content-between my-1">
                      <strong>Shipped at:</strong> <span
                      class="text-secondary">{{ moment(shipment.shipped_at).format('M/D/YY h:mm A') }}</span>
                    </div>
                    <div class="d-flex justify-content-between my-1" v-if="shipment.status == 15">
                      <strong>Delivered at:</strong> <span
                      class="text-secondary">{{ moment(shipment.delivered_at).format('M/D/YY h:mm A') }}</span>
                    </div>
                    <div class="d-flex justify-content-between my-1" v-if="shipment.delivery_date">
                      <strong>Expected Delivery:</strong> <span
                      class="text-secondary">{{ moment.utc(shipment.delivery_date).format('M/D/YY') }}</span>
                    </div>
                    <div v-if="shipment.events.length > 0" class="d-flex justify-content-between my-1">
                      <strong>Last Update:</strong> <span class="text-secondary">{{
                        moment(shipment.events[0].event_date).format('M/D/YY h:mm A')
                      }} {{ shipment.events[0].event_description }} {{
                        shipment.events[0].event_city
                      }}, {{ shipment.events[0].event_state }}</span>
                      <a :href="`https://www.fedex.com/fedextrack?trknbr=${shipment.tracking_number}`"
                         target="_blank"
                         class="d-inline-block mr-5" v-if="shipment.carrier == 'fedex'">
                        FedEx {{ shipment.tracking_number }}
                      </a>
                      <a
                        :href="`http://wwwapps.ups.com/WebTracking/track?track=yes&trackNums=${shipment.tracking_number}`"
                        target="_blank"
                        class="d-inline-block mr-2" v-else-if="shipment.carrier == 'ups'">
                        UPS {{ shipment.tracking_number }}
                      </a>
                    </div>
                  </li>
                </ul>
              </card>
            </div>

            <div class="col-12 col-md-6 col-lg-4">
              <card title="Ship To" class="ship-address h-100">
                <p class="m-0 text-secondary">
                  {{ shipment.address.company_name }}<br/>
                  {{ shipment.address.first_name }} {{ shipment.address.last_name }}<br/>
                  {{ shipment.address.address1 }}
                  {{ shipment.address.address2 }}<br/>
                  {{ shipment.address.city }}, {{ shipment.address.state }} {{ shipment.address.postcode }}
                </p>
              </card>
            </div>
          </div>

          <card v-if="shipment.events.length" title="Shipment Events" class="mt-3" :collapse="true">
            <ul class="list-unstyled">
              <li v-for="track in shipment.events" :key="track.id" class="row py-2 border-bottom">
                <div class="col-12 col-lg-4">{{ moment(track.event_date).format('M/D/YY h:mm A') }}</div>
                <div class="col-12 col-lg-4">{{ track.event_description }}</div>
                <div class="col-12 col-lg-4" v-if="track.event_city && track.event_city.length > 0">{{
                    track.event_city
                  }}, {{ track.event_state }}
                </div>
              </li>
            </ul>
          </card>

          <card title="Packages" class="mt-3">
            <ul class="list-group">
              <li v-for="box in shipment.packages" :key="box.id" class="box-item list-group-item list-group-item-info">
                <p class="font-weight-bold mb-2">{{ box.type }}</p>

                <div class="mt-2">
                  <p class="m-0">{{ box.box_size }}</p>
                  <a :href="box.label" target="_label">Shipping Label</a> |
                  <a :href="route('admin.orders.packing', box)" target="_label">Packing Slip</a>
                </div>
                <div class="items col-sm-6 px-0">
                  <ul class="list-unstyled">
                    <li v-for="item in box.items" :key="item.id" class="row py-2">
                      <div class="col-12 col-lg-4">{{ item.qty }}</div>
                      <div class="col-12 col-lg-4">{{ item.sku }}</div>
                      <div class="col-12 col-lg-4">{{ item.name }}</div>
                    </li>
                  </ul>
                </div>
              </li>
            </ul>

            <div class="mt-2">
              <p class="font-weight-bold">Manual Actions: </p>
              <div v-if="shipment.status == 1 || shipment.status == 2 || shipment.status == 20">
                <inertia-link :href="route('admin.user.shipment.shipped', shipment)" preserve-scroll>Mark Shipped
                </inertia-link>
                <br/>
              </div>
              <div v-else-if="shipment.status == 6">
                <inertia-link :href="route('admin.user.shipment.delivered', shipment)" preserve-scroll>Mark Delivered
                </inertia-link>
                <br/>
              </div>
            </div>
          </card>
        </div>
      </card>
    </template>
  </layout>
</template>
<script>
import Layout from '@/Shared/Admin/Layout'
import FlashMessages from '@/Shared/FlashMessages'
import JetDialogModal from '@/Jetstream/DialogModal'
import JetDangerButton from '@/Jetstream/DangerButton'
import JetButton from '@/Jetstream/Button'
import moment from 'moment'
import Calendar from 'primevue/calendar'
import toastr from 'toastr'
import BreadCrumbs from '@/Shared/BreadCrumbs'
import Card from "@/Shared/Card";
import AppHead from "@/Shared/AppHead";

export default {
  components: {
    FlashMessages,
    Layout,
    JetDialogModal,
    JetDangerButton,
    Calendar,
    BreadCrumbs,
    JetButton,
    Card,
    AppHead,
  },
  props: {
    user: Object,
    items: Array,
    dropships: Array,
    shipmentHistory: Array,
    notes: Array,
    box_types: Array,
    addresses: Array,
    fedex_methods: Object,
    ups_methods: Object,
    mtd: Object,
    isSuper: Boolean,
  },
  data() {
    return {
      today: (new Date()),
    }
  },
  created() {
    this.moment = moment
  },
  methods: {
    
  },
}

</script>

<style>
.gallery-thumbnail img {
  width: 120px;
}

.shipment {
  border: #000 1px solid;
}

.shipment-active .alert {
  position: fixed;
  bottom: 200px;
  right: 200px;
  z-index: 5000;
}

</style>
